<template>
  <div>

    <form-wizard
      color="#498ceb"
      :title="null"
      :subtitle="null"
      layout="horizontal"
      :finish-button-text="$t('message.submit')"
      :back-button-text="$t('message.previous')"
      :next-button-text="$t('message.next')"
      :hide-buttons="readOnly"
      class="wizard-vertical mb-3"
      ref="wizardForm"
      @on-change="handleTabChange"
    >
      <template v-slot:footer="slotProps">
        <div class="wizard-footer d-flex justify-content-between align-items-center">
          <b-button
            v-if="!readOnly"
            variant="outline-secondary"
            @click="goToPreviousTab"
            :disabled="slotProps.activeTabIndex === 0"
          >
            < &nbsp; {{ $t('message.previous') }}
          </b-button>

          <div class="ml-auto d-flex align-items-center">
            <b-button
              v-if="!readOnly && !slotProps.isLastStep"
              variant="primary"
              class="mr-2"
              @click="goToNextTab"
            >
              {{ $t('message.next') }} &nbsp; >
            </b-button>

            <b-button
              v-if="!readOnly"
              variant="success"
              @click="handleSubmit"
            >
              {{ $t('message.submit') }}
            </b-button>
          </div>
        </div>
      </template>

      <tab-content
        :title="$t('label.identification')"
      >
        <validation-observer
          ref="identificationTab"
          tag="form"
        >
          <Identification
            ref="identification"
            :data="data"
            :crimes="crimes"
            :crimesData="crimesData"
            :areas="locations"
            :readOnly="readOnly"
            :residualRisks="residualRiskOptions"
            :probabilityOptions="probabilityOptions"
            :impactOptions="impactOptions"
            :companies="companies"
            :ids="ids"
          />
        </validation-observer>
      </tab-content>

      <tab-content
        :title="$t('label.raci')"
      >
        <validation-observer
          ref="raciTab"
          tag="form"
        >
          <RACI
            ref="raciRef"
            :workers="workersSupervised"
            :roles="roles"
            :data="data"
            :readOnly="readOnly"
          />
        </validation-observer>
      </tab-content>

      <tab-content
        :title="$t('label.activities')"
      >
        <validation-observer
          ref="activitiesTab"
          tag="form"
        >
          <Activities
            ref="activitiesRef"
            :data="reformatedData && reformatedData.activities ? reformatedData.activities : {}"
            :readOnly="readOnly"
            @updActivities="(value) => addedActivities = value"
          />
        </validation-observer>
      </tab-content>

      <tab-content
        :title="$t('label.behaviours')"
      >
        <validation-observer
          ref="behavioursTab"
          tag="form"
        >
          <Behaviours
            ref="behavioursForm"
            :data="reformatedData && reformatedData.behaviours ? reformatedData.behaviours : []"
            :added-activities="addedActivities"
            :readOnly="readOnly"
          />
        </validation-observer>
      </tab-content>

    </form-wizard>

    <!-- Audit Log Table -->
    <audit-log-table
      v-if="data"
      :history="data.history || []"
      :field-translations="{
        name: $t('label.processName'),
        code: $t('label.code'),
        goal: $t('label.goal'),
        assumptions: $t('label.assumptions'),
        crimes: $t('label.crimes'),
        areas: $t('label.areas'),
        controls: $t('label.activities'),
        responsibles: $t('label.responsibles'),
        leadResponsibles: $t('label.leadResponsibles'),
        evaluator: $t('label.evaluator'),
        evaluated: $t('label.evaluated'),
        residualRisk: $t('label.residualRisk'),
        residualRiskProbability: $t('label.residualRiskProbability'),
        residualRiskImpact: $t('label.residualRiskImpact'),
        inherentRisk: $t('label.inherentRisk'),
        inherentRiskProbability: $t('label.inherentRiskProbability'),
        inherentRiskImpact: $t('label.inherentRiskImpact'),
        company: $t('label.company'),
        id: $t('label.id'),
        description: $t('label.description')
      }"
    />

  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { FormWizard, TabContent } from 'vue-form-wizard'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import useNotifications from '@/composables/useNotifications'
import Identification from '@/views/organization/process/shared/Identification.vue'
import RACI from '@/views/organization/process/shared/RACI.vue'
import Activities from '@/views/organization/process/shared/Activities.vue'
import Behaviours from '@/views/organization/process/shared/Behaviours.vue'
import AuditLogTable from '@/components/AuditLogTable.vue'
import { ref, onMounted, watch } from '@vue/composition-api'
import useCommon from '../../useCommon'
import i18n from '@/libs/i18n'
import { residualRiskOptions, probabilityOptions, impactOptions } from '@/constants'

export default {
  components: {
    Identification,
    RACI,
    Activities,
    Behaviours,
    AuditLogTable,
    FormWizard,
    TabContent,
    ValidationObserver,
  },

  props: ['data', 'isSubmitting', 'readOnly'],

  setup(props, { emit }) {
    const {
      getWorkersSupervisedForDropDown, workersSupervised, getRolesForDropDown, roles, getCrimesForDropDown, crimes, crimesData, getLocationsForDropDown, locations, getCompaniesForDropDown, companies, getIDsForDropDown, ids
    } = useCommon()
    const { showErrorMessage } = useNotifications()

    const addedActivities = ref([])
    const identification = ref(null)
    const responsibilities = ref(null)
    const qualifications = ref(null)
    const identificationTab = ref(null)
    const activitiesTab = ref(null)
    const behavioursTab = ref(null)
    const behavioursForm = ref(null)
    const raciTab = ref(null)
    const raciRef = ref(null)
    const activitiesRef = ref(null)
    const reformatedData = ref({})
    const wizardForm = ref(null)

    onMounted(() => {
      getRolesForDropDown()
      getWorkersSupervisedForDropDown()
      getCrimesForDropDown()
      getLocationsForDropDown()
      getCompaniesForDropDown()
      getIDsForDropDown()
      // await getMetadataForDropDown({category: "process"})

      // Make all form tabs clickable
      if (wizardForm.value) {
        wizardForm.value.maxStep = wizardForm.value.tabCount - 1
        wizardForm.value.tabs.forEach(t => t.checked = true)
      }
    })

    const formSubmitted = async () => {
      if (props.isSubmitting) return

      const payload = {
        ...identification.value.getData(),
        ...raciRef.value.getData(),
        ...activitiesRef.value.getData(),
        ...behavioursForm.value.getData(),
      }

      /* INFO:: WHY THIS NEW FORMAT ?
       While this was reimplementing developer had changed the original data structure and
       at that moment backend was completed with previous structure. In order to have a common pattern we have reformatted
       the payload here.
       */
      // eslint-disable-next-line no-unused-vars
      const { raci, activities, behaviours } = payload
      const reformatPayload = payload.identification
      // reformatPayload.crimes = { link: reformatPayload.crimes?.map(e => e.value) || [] }
      // reformatPayload.areas = { link: reformatPayload.areas?.map(e => e.value) || [] }
      if (raci.responsibles) reformatPayload.responsibles = raci.responsibles?.map(e => e.value) || []
      if (raci.leadResponsibles) reformatPayload.leadResponsibles = raci.leadResponsibles?.map(e => e.value) || []
      if (raci.evaluator) reformatPayload.evaluator = raci.evaluator.value
      if (raci.evaluated) reformatPayload.evaluated = raci.evaluated.value
      if (reformatPayload.company) reformatPayload.company = reformatPayload.company.value
      if (reformatPayload.id) reformatPayload.id = reformatPayload.id.value
      // reformatPayload.role = { link: raci.role.value }
      reformatPayload.description = activities.description
      reformatPayload.controls = Array.from(new Set(activities.list || []))
      // reformatPayload.activities = activities.list.map((activity, index) => ({
      //   name: activity,
      //   behaviours: behaviours[index].behaviours,
      // }))
      // reformatPayload.requireImage = reformatPayload.requireImage.value
      // if (!reformatPayload.metadata.length) delete reformatPayload.metadata
      // else reformatPayload.metadata = reformatPayload.metadata.map(e => e.value)

      emit('submit', reformatPayload)
    }

    const validateIdentificationForm = async () => new Promise((resolve, reject) => {
      identificationTab.value.validate().then(success => {
        if (success) {
          resolve(true)
        } else {
          reject()
        }
      })
    })

    const validateRaciForm = async () => new Promise((resolve, reject) => {
      raciTab.value.validate().then(success => {
        if (success) {
          resolve(true)
        } else {
          reject()
        }
      })
    })

    const validateActivitiesForm = async () => new Promise((resolve, reject) => {
      // Check if there are missing or repeated controls
      const activitiesData = activitiesRef.value.getData()

      if (activitiesData.isControlFormActive) {
        showErrorMessage(i18n.t('message.mustCloseControlForm'))
        return reject()
      }

      const controls = activitiesData?.activities?.list || []
      const controlRegistry = {}

      for (const control of controls) {
        if (!control) {
          showErrorMessage(i18n.t('message.requiredFieldsIncomplete'))
          return reject()
        }

        if (controlRegistry[control]) {
          showErrorMessage(i18n.t('message.controlsRepeated'))
          return reject()
        }

        controlRegistry[control] = 1
      }

      activitiesTab.value.validate().then(success => {
        if (success) {
          resolve(true)
        } else {
          reject()
        }
      })
    })

    const validateBehavioursForm = async () => new Promise((resolve, reject) => {
      behavioursTab.value.validate().then(success => {
        if (success) {
          resolve(true)
        } else {
          reject()
        }
      })
    })

    watch(() => props.data, value => {
      /* INFO:: WHY THIS NEW FORMAT ?
       While this was reimplementing developer had changed the original data structure and
       at that moment backend was completed with previous structure. In order to have a common pattern we have reformatted
       the payload here.
       */
      if (value) {
        reformatedData.value.identification = {
          name: value.name,
          code: value.code,
          goal: value.goal,
          assumptions: value.assumptions,
        }

        reformatedData.value.raci = {
          role: value.role,
          responsibles: value.responsibles,
          leadResponsibles: value.leadResponsibles,
          evaluator: value.evaluator,
          evaluated: value.evaluated,
        }
        // reformatedData.value.activities = { description: value.description, list: value.activities.map(activity => activity.name) }
        reformatedData.value.activities = { description: value.description, list: value.controls }
        // reformatedData.value.behaviours = value.activities.map(activity => ({
        //   activity: activity.name,
        //   behaviours: activity.behaviours,
        // }))
      }
    }, { deep: true, immediate: true })

    const goToPreviousTab = () => {
      if (wizardForm.value) {
        wizardForm.value.prevTab()
      }
    }

    const goToNextTab = async () => {
      const index = wizardForm.value.activeTabIndex

      const validators = [
        validateIdentificationForm,
        validateRaciForm,
        validateActivitiesForm,
        validateBehavioursForm,
      ]

      const validate = validators[index]

      try {
        await validate()
        wizardForm.value.nextTab()
      } catch (err) {
        // Error de validación, no avanza
      }
    }

    const handleTabChange = async (oldTabIndex, newTabIndex) => {
      if (newTabIndex === 3) {
        const activitiesData = activitiesRef.value.getData()
        const controls = activitiesData?.activities?.list || []
        behavioursForm.value.createForm(controls)
      }
    }

    const validateAllTabs = async () => {
      const validators = [
        validateIdentificationForm,
        validateRaciForm,
        validateActivitiesForm,
        validateBehavioursForm,
      ]

      const wizard = wizardForm.value

      for (let i = 0; i < validators.length; i++) {
        try {
          await validators[i]()
        } catch (error) {
          const failedTab = wizard.tabs[i]
          const tabTitle = failedTab.title

          if (i !== wizard.activeTabIndex) {
            showErrorMessage(i18n.tc('message.tabValidationError', 0, { tabTitle }))
          }

          throw error
        }
      }
    }

    const handleSubmit = async () => {
      try {
        await validateAllTabs()
        formSubmitted()
      } catch (error) {
        // Si alguna validación falla, no enviamos
      }
    }

    return {
      formSubmitted,
      identification,
      responsibilities,
      qualifications,
      identificationTab,
      raciTab,
      raciRef,
      activitiesRef,
      activitiesTab,
      behavioursTab,
      validateIdentificationForm,
      validateRaciForm,
      validateActivitiesForm,
      validateBehavioursForm,
      workersSupervised,
      roles,
      // metadata,
      addedActivities,
      behavioursForm,
      reformatedData,
      crimes,
      crimesData,
      locations,
      wizardForm,
      residualRiskOptions,
      probabilityOptions,
      impactOptions,
      goToPreviousTab,
      goToNextTab,
      handleSubmit,
      handleTabChange,
      companies,
      ids,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-wizard.scss";
@import "@core/scss/vue/libs/vue-select.scss";
</style>
